import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import SocialProof from "@/components/SocialProof";
import Footer from "@/components/Footer";
import {
  Sparkle, Wand2, Mic, Square, ArrowRight, Loader2, Copy, Check, Play, Pause, Crown, Heart, Building2, User,
  Laugh, Leaf, PartyPopper, Moon, Gem,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The Sovereign Library — same Vibe Selector as the logged-in Design Studio, open to guests too
// (the "3-second Showroom hook" — a guest seeing their own Zodiac constellation in gold foil).
const GUEST_VIBES = [
  { id: "", label: "Classic", icon: Sparkle },
  { id: "funny", label: "Funny", icon: Laugh },
  { id: "seasonal", label: "Seasonal", icon: Leaf },
  { id: "holiday", label: "Holiday", icon: PartyPopper },
  { id: "regal", label: "Regal", icon: Crown },
  { id: "celestial", label: "Celestial", icon: Moon },
];

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Guest curation
  const [name, setName] = useState("");
  const [occasion, setOccasion] = useState("Birthday");
  const [curating, setCurating] = useState(false);
  const [curation, setCuration] = useState(null);
  const [error, setError] = useState("");
  const [vibe, setVibe] = useState("");
  const [birthDate, setBirthDate] = useState("");

  // Maison Classics — instant guest templates
  const [templates, setTemplates] = useState([]);
  const [usingTemplateId, setUsingTemplateId] = useState("");

  // Voice viral loop
  const [recording, setRecording] = useState(false);
  const [hubUrl, setHubUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioElRef = useRef(null);

  // Favorites
  const [favorites, setFavorites] = useState([]);
  // Showroom Entry — "For You" / "For Business" (both lead to the same Experience the Magic demo below)
  const [entryPath, setEntryPath] = useState("you");

  useEffect(() => {
    axios.get(`${API}/showroom/favorites`).then((r) => setFavorites(r.data.favorites || [])).catch(() => {});
    axios.get(`${API}/templates`).then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, []);

  const curate = async () => {
    if (!name.trim()) { setError("Add a name to preview the magic."); return; }
    if (vibe === "celestial" && !birthDate) { setError("Enter a birth date to unlock Celestial Insight."); return; }
    setCurating(true); setError(""); setCuration(null); setHubUrl("");
    try {
      const r = await axios.post(`${API}/showroom/curate`, { name, occasion, vibe: vibe || null, birth_date: vibe === "celestial" ? birthDate : null });
      setCuration(r.data);
      // remember guest token to claim on signup
      try {
        const prev = JSON.parse(localStorage.getItem("gifted_guest_tokens") || "[]");
        localStorage.setItem("gifted_guest_tokens", JSON.stringify([...prev, r.data.guest_token]));
      } catch (e) { console.error("Could not persist guest token:", e); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError((d && typeof d === "object" ? d.message : d) || "Curation is busy — please try again.");
    } finally { setCurating(false); }
  };

  const pickGuestTemplate = async (templateId) => {
    if (!name.trim()) { setError("Add a name first, then pick a Maison Classic."); return; }
    setUsingTemplateId(templateId); setError(""); setCuration(null); setHubUrl("");
    try {
      const r = await axios.post(`${API}/showroom/use-template`, { template_id: templateId, name, occasion });
      setCuration(r.data);
      try {
        const prev = JSON.parse(localStorage.getItem("gifted_guest_tokens") || "[]");
        localStorage.setItem("gifted_guest_tokens", JSON.stringify([...prev, r.data.guest_token]));
      } catch (e) { console.error("Could not persist guest token:", e); }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not use that template.");
    } finally { setUsingTemplateId(""); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (audioElRef.current) audioElRef.current.src = URL.createObjectURL(blob);
        const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(blob); });
        try {
          const r = await axios.post(`${API}/showroom/voice`, { curation_id: curation.curation_id, audio_base64: b64, mime: "audio/webm" });
          setHubUrl(r.data.hub_url);
        } catch (e) { setError("Could not attach voice."); }
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
      setTimeout(() => { if (mediaRef.current?.state === "recording") mediaRef.current.stop(); setRecording(false); }, 15000);
    } catch (e) { setError("Microphone access is needed to record."); }
  };
  const stopRec = () => { if (mediaRef.current?.state === "recording") mediaRef.current.stop(); setRecording(false); };

  const copyHub = () => { navigator.clipboard?.writeText(hubUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <div className="min-h-screen">
      {/* Top bar */}
      <header className="sticky top-0 z-40 glass border-b border-border/40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center"><Sparkle className="w-4 h-4 text-white" /></div>
            <span className="gifted-brand text-xl">GIFTED</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="cta-login" onClick={() => navigate(user ? "/dashboard" : "/login")}
              className="text-sm px-4 py-2 rounded-2xl surface text-foreground hover:border-primary/40">
              {user ? "Manage Estate" : "Manage Estate"}
            </button>
            <button data-testid="cta-signup" onClick={() => navigate(user ? "/dashboard" : "/login")}
              className="text-sm px-5 py-2 rounded-2xl bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90 flex items-center gap-1.5">
              <Crown className="w-4 h-4" /> Enter the Maison
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-sm mb-5">
          <Sparkle className="w-4 h-4 gold-text" /> The Maison Showroom
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl leading-tight mb-4">
          The Future of <span className="shimmer-gold italic">Personalized Sentiment</span>
        </h1>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
          AI-crafted cards, a recorded voice they&apos;ll keep forever, and effortless mailing — so the people you love always feel seen.
        </p>

        {/* Showroom Entry: For You / For Business — both lead to the same 3D Unboxing + Royal Voice preview below */}
        <div className="inline-flex items-center gap-1 p-1 rounded-2xl surface mb-2" data-testid="showroom-entry-toggle">
          <button data-testid="entry-for-you" onClick={() => setEntryPath("you")}
            className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-xl transition-colors ${entryPath === "you" ? "bg-gradient-to-r from-primary to-secondary text-white" : "text-muted-foreground hover:text-foreground"}`}>
            <User className="w-3.5 h-3.5" /> For You
          </button>
          <button data-testid="entry-for-business" onClick={() => setEntryPath("business")}
            className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-xl transition-colors ${entryPath === "business" ? "bg-gradient-to-r from-primary to-secondary text-white" : "text-muted-foreground hover:text-foreground"}`}>
            <Building2 className="w-3.5 h-3.5" /> For Business
          </button>
        </div>
      </section>

      {/* Experience the Magic — guest curation (same demo for both entry paths) */}
      <section className="max-w-4xl mx-auto px-6 mb-16" data-testid="experience-magic">
        <div className="surface rounded-[2rem] p-8 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-serif text-3xl mb-2 flex items-center gap-2"><Wand2 className="w-6 h-6 text-primary" /> Experience the Magic</h2>
            <p className="text-muted-foreground text-sm mb-5" data-testid="entry-tagline">
              {entryPath === "business"
                ? "See exactly what your team's cards will look and sound like — try the same live curation your recipients will experience, with on-brand fulfillment behind the scenes."
                : "Type a name and occasion — watch a bespoke card curate itself, live. No account needed."}
            </p>
            <input data-testid="guest-name" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full mb-3 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Loved one's name (e.g. Mom)" />
            <input data-testid="guest-occasion" value={occasion} onChange={(e) => setOccasion(e.target.value)}
              className="w-full mb-4 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Occasion" />

            {/* The Sovereign Library — Vibe Selector, free for guests too */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mb-3" data-testid="guest-vibe-selector">
              {GUEST_VIBES.map((v) => {
                const Icon = v.icon;
                return (
                  <button key={v.id || "classic"} type="button" data-testid={`guest-vibe-${v.id || "classic"}`} onClick={() => setVibe(v.id)}
                    className={`text-[10px] rounded-lg py-1.5 px-1 border flex flex-col items-center gap-1 ${vibe === v.id ? "bg-primary/15 text-primary border-primary/30" : "surface text-muted-foreground"}`}>
                    <Icon className="w-3.5 h-3.5" /> {v.label}
                  </button>
                );
              })}
            </div>
            {vibe === "celestial" && (
              <input data-testid="guest-birth-date" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)}
                className="w-full mb-3 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Birth date for zodiac sign" />
            )}

            <button data-testid="guest-curate-btn" onClick={curate} disabled={curating}
              className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-3.5 hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2">
              {curating ? <><Loader2 className="w-4 h-4 animate-spin" /> Curating your card… (~15s)</> : <><Sparkle className="w-4 h-4" /> Curate my card</>}
            </button>
            {error && <div data-testid="guest-error" className="text-sm text-red-500 mt-2">{error}</div>}

            {/* Maison Classics — instant, no AI wait */}
            {templates.length > 0 && (
              <div className="mt-4" data-testid="guest-template-library">
                <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5"><Gem className="w-3 h-3 gold-text" /> Or pick a Maison Classic — instant, no wait</div>
                <div className="grid grid-cols-6 gap-1.5">
                  {templates.slice(0, 12).map((t) => (
                    <button key={t.id} type="button" data-testid="guest-template-item" onClick={() => pickGuestTemplate(t.id)}
                      disabled={!!usingTemplateId} title={t.title}
                      className="relative rounded-lg overflow-hidden border border-border hover:border-primary/50 disabled:opacity-60 aspect-square">
                      <img src={`data:image/png;base64,${t.image_thumb_base64}`} alt={t.title} className="w-full h-full object-cover" />
                      {usingTemplateId === t.id && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><Loader2 className="w-3 h-3 text-white animate-spin" /></div>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="min-h-[260px] flex items-center justify-center">
            {curation ? (
              <div className="w-full unbox-reveal" data-testid="guest-result">
                <div className="rounded-2xl overflow-hidden border border-border mb-3 relative">
                  <img src={`data:image/png;base64,${curation.image_base64}`} alt="" className="w-full h-52 object-cover" />
                  {/* Maison 3D unboxing lid */}
                  <div className="unbox-lid absolute inset-0 bg-gradient-to-b from-[#efe3d0] to-[#e6d3b8] flex items-center justify-center" data-testid="unbox-lid">
                    <Sparkle className="w-8 h-8 gold-text" />
                  </div>
                </div>
                <p className="font-serif italic text-center text-foreground/90">&ldquo;{curation.message}&rdquo;</p>

                {/* Viral loop — voice card */}
                <div className="mt-4 surface rounded-2xl p-4">
                  <div className="text-sm font-medium flex items-center gap-1.5 mb-2"><Mic className="w-4 h-4 text-primary" /> Send Your First Voice Card — Free</div>
                  {!hubUrl ? (
                    <div className="flex gap-2">
                      {!recording ? (
                        <button data-testid="guest-record-btn" onClick={startRec} className="flex-1 text-sm surface rounded-xl py-2 hover:border-primary/40 flex items-center justify-center gap-1.5"><Mic className="w-4 h-4" /> Record 15s</button>
                      ) : (
                        <button data-testid="guest-stop-btn" onClick={stopRec} className="flex-1 text-sm bg-red-500/15 text-red-500 rounded-xl py-2 gold-pulse-ring flex items-center justify-center gap-1.5"><Square className="w-4 h-4" /> Stop</button>
                      )}
                    </div>
                  ) : (
                    <div data-testid="guest-hub" className="space-y-2">
                      <div className="flex items-center gap-2 surface rounded-xl p-2">
                        <input readOnly value={hubUrl} className="flex-1 bg-transparent text-xs text-muted-foreground truncate outline-none" />
                        <button onClick={copyHub} className="text-xs flex items-center gap-1 text-primary">{copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}</button>
                      </div>
                      <a href={hubUrl} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1"><Play className="w-3 h-3" /> Preview the Maison Hub</a>
                    </div>
                  )}
                  <audio ref={audioElRef} controls className="w-full mt-2 hidden" />
                </div>

                <button data-testid="guest-signup-btn" onClick={() => navigate("/login")}
                  className="w-full mt-3 bg-gradient-to-r from-[#c9a24a] to-primary text-white rounded-2xl py-3 hover:opacity-90 flex items-center justify-center gap-2">
                  <Crown className="w-4 h-4" /> Enter the Maison to save &amp; send <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="text-center text-muted-foreground border border-dashed border-border rounded-2xl w-full py-16">
                <Sparkle className="w-8 h-8 mx-auto mb-2 gold-text" />
                Your live card preview appears here.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Maison Favorites */}
      {favorites.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 mb-20" data-testid="maison-favorites">
          <div className="text-center mb-6">
            <h2 className="font-serif text-3xl">Maison Favorites</h2>
            <p className="text-muted-foreground text-sm">A glimpse of the craftsmanship.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {favorites.map((f, i) => (
              <div key={`${f.occasion}-${i}`} className="surface rounded-2xl overflow-hidden card-soft">
                <img src={`data:image/jpeg;base64,${f.image_base64}`} alt="" className="w-full h-32 object-cover" />
                <div className="text-[11px] text-center text-muted-foreground py-1.5">{f.occasion}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Gated Tiers teaser */}
      <section className="max-w-5xl mx-auto px-6 mb-16" data-testid="landing-tiers">
        <div className="text-center mb-6">
          <h2 className="font-serif text-3xl">Choose your Maison</h2>
          <p className="text-muted-foreground text-sm">Pay-per-card, a one-time Credit Pack, or the Business Team Plan.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { name: "Boutique", price: "$9.99", tag: "per card", pts: ["Manual design studio", "Silver Seal courtesy sends"] },
            { name: "Credit Pack", price: "$79–$349", tag: "10 / 25 / 50 cards", pts: ["AI Magic Wand + Circles", "Unlocks every premium feature, forever"], hot: true },
            { name: "Business Team", price: "$29/mo", tag: "for teams", pts: ["Bulk CSV + AI Address Auditor", "Scheduled Sends + Corporate Seal"] },
          ].map((t) => (
            <div key={t.name} className={`surface rounded-[1.5rem] p-6 text-center ${t.hot ? "ring-2 ring-primary/40" : ""}`}>
              <div className="font-serif text-2xl">{t.name}</div>
              <div className="gold-text text-sm mb-3">{t.price} · {t.tag}</div>
              <ul className="text-sm text-muted-foreground space-y-1.5 mb-5">{t.pts.map((p, i) => <li key={i}>{p}</li>)}</ul>
              <button onClick={() => navigate("/membership")} className="w-full surface rounded-2xl py-2.5 text-primary hover:border-primary/40 text-sm">Compare tiers</button>
            </div>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="max-w-3xl mx-auto px-6 pb-24 text-center">
        <Heart className="w-8 h-8 mx-auto mb-4 text-secondary/70" />
        <h2 className="font-serif text-3xl mb-3">Never miss a moment that matters.</h2>
        <button data-testid="cta-signup-bottom" onClick={() => navigate("/login")}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white px-7 py-3.5 rounded-2xl hover:opacity-90">
          <Crown className="w-4 h-4" /> Enter the Maison
        </button>
      </section>

      <SocialProof />
      <Footer />
    </div>
  );
};

export default Landing;
